import { supabase } from '@/integrations/supabase/client';

// ModeloCarregador agora aceita qualquer string para permitir modelos customizados
export type ModeloCarregador = string;

// Modelos padrão iniciais (podem ser expandidos dinamicamente)
export const MODELOS_CARREGADOR_PADRAO: ModeloCarregador[] = ['Wallbox 7kW', 'Wallbox 11kW', 'Wallbox 22kW'];

// Função para obter todos os modelos disponíveis (busca dinamicamente do banco)
export async function obterModelosDisponiveis(): Promise<ModeloCarregador[]> {
  const { supabase } = await import('@/integrations/supabase/client');
  const { data, error } = await supabase
    .from('carregadores_valores_propostas_globais')
    .select('modelo_carregador')
    .order('modelo_carregador');
  
  if (error) {
    console.error('Erro ao buscar modelos:', error);
    return MODELOS_CARREGADOR_PADRAO;
  }
  
  return (data ?? []).map((r: any) => r.modelo_carregador);
}

export interface ComissaoParceiroModelo {
  id: string;
  parceiro_id: string;
  modelo_carregador: ModeloCarregador;
  valor_comissao: number;
  created_at: string;
  updated_at: string;
}

export interface ParceiroComissaoPorModelo {
  id: string;
  nome: string;
  role: string;
  valor_7kw: number;
  valor_11kw: number;
  valor_22kw: number;
}

export interface ResumoComissaoCarregador {
  parceiro_id: string;
  parceiro_nome: string;
  parceiro_pai_nome?: string | null;
  qtd_contratos: number;
  valor_comissao_unitario: number;
  total_comissoes: number;
  total_pago: number;
  saldo: number;
}

const TABLE = 'carregadores_comissao_parceiro_modelo';
const PAGAMENTOS_TABLE = 'carregadores_comissao_pagamentos';
const TABLE_COMISSAO_PAI = 'carregadores_comissao_parceiro_pai';
const TABLE_COMISSAO_GLOBAIS = 'carregadores_comissao_globais';

/** Comissão por parceiro pai: valor em R$ que o pai recebe por contrato fechado por cada filho (por modelo) */
export interface ParceiroFilhoComissaoPai {
  parceiro_filho_id: string;
  parceiro_filho_nome: string;
  valor_7kw: number;
  valor_11kw: number;
  valor_22kw: number;
  /** Número de contratos fechados pelo parceiro (rede) */
  qtd_contratos?: number;
  /** Total de comissões que o parceiro pai deve receber pelas vendas do filho */
  total_comissoes?: number;
  /** Total já pago ao parceiro pai referente a esse filho */
  total_pago?: number;
  /** Saldo a receber (total_comissoes - total_pago) */
  saldo?: number;
}

export interface ParceiroPaiComissao {
  parceiro_pai_id: string;
  parceiro_pai_nome: string;
  filhos: ParceiroFilhoComissaoPai[];
}

/** Valor padrão global de comissão por modelo (parceiros e parceiros pai) */
export interface ComissaoGlobal {
  id: string;
  modelo_carregador: ModeloCarregador;
  valor_comissao_parceiro: number;
  valor_comissao_parceiro_pai: number;
  created_at: string;
  updated_at: string;
}

/**
 * Lista parceiros com valores de comissão por modelo (7kW, 11kW, 22kW).
 * Apenas admin pode chamar.
 */
export async function listarParceirosComComissaoPorModelo(): Promise<ParceiroComissaoPorModelo[]> {
  const { data: parceiros, error: errParceiros } = await supabase
    .from('profiles')
    .select('id, nome, role')
    .eq('role', 'parceiro')
    .order('nome');

  if (errParceiros) throw errParceiros;

  const { data: comissoes, error: errComissoes } = await supabase
    .from(TABLE)
    .select('parceiro_id, modelo_carregador, valor_comissao');

  if (errComissoes) throw errComissoes;

  const map = new Map<string, { 7: number; 11: number; 22: number }>();
  (comissoes ?? []).forEach((c: any) => {
    const key = c.parceiro_id;
    if (!map.has(key)) map.set(key, { 7: 0, 11: 0, 22: 0 });
    const v = map.get(key)!;
    if (c.modelo_carregador === 'Wallbox 7kW') v[7] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 11kW') v[11] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 22kW') v[22] = Number(c.valor_comissao);
  });

  return (parceiros ?? []).map((p: any) => {
    const cfg = map.get(p.id) ?? { 7: 0, 11: 0, 22: 0 };
    return {
      id: p.id,
      nome: p.nome ?? 'Sem nome',
      role: p.role,
      valor_7kw: cfg[7],
      valor_11kw: cfg[11],
      valor_22kw: cfg[22],
    };
  });
}

/**
 * Obtém a comissão do parceiro logado por modelo (para visualização).
 * Usa valores padrão globais quando não há valor específico configurado.
 */
export async function obterMinhaComissaoPorModelo(parceiroId: string): Promise<ParceiroComissaoPorModelo | null> {
  const { data: parceiro, error: errParceiro } = await supabase
    .from('profiles')
    .select('id, nome, role')
    .eq('id', parceiroId)
    .eq('role', 'parceiro')
    .single();

  if (errParceiro || !parceiro) return null;

  const { data: comissoes, error: errComissoes } = await supabase
    .from(TABLE)
    .select('modelo_carregador, valor_comissao')
    .eq('parceiro_id', parceiroId);

  if (errComissoes) throw errComissoes;

  const defaults = await obterDefaultsComissaoParceiro();
  const cfg = { 7: 0, 11: 0, 22: 0 };
  (comissoes ?? []).forEach((c: any) => {
    if (c.modelo_carregador === 'Wallbox 7kW') cfg[7] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 11kW') cfg[11] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 22kW') cfg[22] = Number(c.valor_comissao);
  });

  return {
    id: parceiro.id,
    nome: parceiro.nome ?? 'Sem nome',
    role: parceiro.role,
    valor_7kw: cfg[7] || defaults[7],
    valor_11kw: cfg[11] || defaults[11],
    valor_22kw: cfg[22] || defaults[22],
  };
}

/**
 * Salva o valor de comissão de um parceiro para um modelo específico (admin).
 */
export async function salvarComissaoParceiroModelo(
  parceiroId: string,
  modelo: ModeloCarregador,
  valorComissao: number
): Promise<ComissaoParceiroModelo> {
  const valor = Math.max(0, valorComissao);

  const { data: existing } = await supabase
    .from(TABLE)
    .select('id')
    .eq('parceiro_id', parceiroId)
    .eq('modelo_carregador', modelo)
    .maybeSingle();

  if (existing?.id) {
    const { data, error } = await supabase
      .from(TABLE)
      .update({ valor_comissao: valor })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from(TABLE)
    .insert({ parceiro_id: parceiroId, modelo_carregador: modelo, valor_comissao: valor })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Resumo de comissões carregadores: contratos fechados, total comissões, pago e saldo.
 * Admin: todos os parceiros; Parceiro: apenas próprio.
 */
export async function getResumoComissaoCarregadores(parceiroId?: string | null): Promise<ResumoComissaoCarregador[]> {
  const { data, error } = await supabase.rpc('get_carregadores_comissao_resumo', {
    p_parceiro_id: parceiroId || null,
  });
  if (error) throw error;
  return (data ?? []).map((r: any) => ({
    parceiro_id: r.parceiro_id,
    parceiro_nome: r.parceiro_nome ?? 'Sem nome',
    parceiro_pai_nome: r.parceiro_pai_nome ?? null,
    qtd_contratos: Number(r.qtd_contratos) || 0,
    valor_comissao_unitario: Number(r.valor_comissao_unitario) || 0,
    total_comissoes: Number(r.total_comissoes) || 0,
    total_pago: Number(r.total_pago) || 0,
    saldo: Number(r.saldo) || 0,
  }));
}

/**
 * Registra pagamento de comissão de carregadores ao parceiro (admin).
 */
export async function registrarPagamentoCarregadores(
  parceiroId: string,
  valor: number,
  dataPagamento: string,
  observacao?: string
): Promise<{ id: string }> {
  const { data: user } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from(PAGAMENTOS_TABLE)
    .insert({
      parceiro_id: parceiroId,
      valor,
      data_pagamento: dataPagamento,
      observacao: observacao ?? null,
      created_by: user.data.user?.id ?? null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Lista valores padrão globais de comissão (parceiros e parceiros pai) por modelo.
 * Usado na seção "Valores Padrão Globais de Comissões" e como base na "Comissões por Parceiro Pai".
 */
const ORDEM_MODELOS_COMISSAO: Record<string, number> = {
  'Wallbox 7kW': 1,
  'Wallbox 11kW': 2,
  'Wallbox 22kW': 3,
};

export async function listarComissaoGlobais(): Promise<ComissaoGlobal[]> {
  const { data, error } = await supabase
    .from(TABLE_COMISSAO_GLOBAIS)
    .select('*');

  if (error) throw error;
  const list = (data ?? []).map((r: any) => ({
    id: r.id,
    modelo_carregador: r.modelo_carregador,
    valor_comissao_parceiro: Number(r.valor_comissao_parceiro) || 0,
    valor_comissao_parceiro_pai: Number(r.valor_comissao_parceiro_pai) || 0,
    created_at: r.created_at,
    updated_at: r.updated_at,
  }));
  return list.sort((a, b) => {
    const ordemA = ORDEM_MODELOS_COMISSAO[a.modelo_carregador] ?? 999;
    const ordemB = ORDEM_MODELOS_COMISSAO[b.modelo_carregador] ?? 999;
    return ordemA - ordemB;
  });
}

/**
 * Atualiza os valores padrão globais de comissão para um modelo (parceiros e parceiros pai).
 * Apenas admin pode chamar. Se o modelo não existir, insere.
 */
export async function atualizarComissaoGlobal(
  modelo: ModeloCarregador,
  valorComissaoParceiro: number,
  valorComissaoParceiroPai: number
): Promise<ComissaoGlobal> {
  const valorParceiro = Math.max(0, valorComissaoParceiro);
  const valorPai = Math.max(0, valorComissaoParceiroPai);
  const { data: existing } = await supabase
    .from(TABLE_COMISSAO_GLOBAIS)
    .select('id')
    .eq('modelo_carregador', modelo)
    .maybeSingle();

  if (existing?.id) {
    const { data, error } = await supabase
      .from(TABLE_COMISSAO_GLOBAIS)
      .update({
        valor_comissao_parceiro: valorParceiro,
        valor_comissao_parceiro_pai: valorPai,
      })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return {
      id: data.id,
      modelo_carregador: data.modelo_carregador,
      valor_comissao_parceiro: Number(data.valor_comissao_parceiro) || 0,
      valor_comissao_parceiro_pai: Number(data.valor_comissao_parceiro_pai) || 0,
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  }
  const { data, error } = await supabase
    .from(TABLE_COMISSAO_GLOBAIS)
    .insert({
      modelo_carregador: modelo,
      valor_comissao_parceiro: valorParceiro,
      valor_comissao_parceiro_pai: valorPai,
    })
    .select()
    .single();
  if (error) throw error;
  return {
    id: data.id,
    modelo_carregador: data.modelo_carregador,
    valor_comissao_parceiro: Number(data.valor_comissao_parceiro) || 0,
    valor_comissao_parceiro_pai: Number(data.valor_comissao_parceiro_pai) || 0,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
}

/**
 * Lista parceiros pai (pode_criar_rede_parceiros) com seus parceiros filho e valores de comissão configurados.
 * Quando não houver valor específico para (pai, filho, modelo), usa o valor padrão global.
 * Apenas admin pode chamar. Usado na seção "Comissões por Parceiro Pai".
 */
export async function listarComissoesParceiroPai(): Promise<ParceiroPaiComissao[]> {
  const { data: globais, error: errGlobais } = await supabase
    .from(TABLE_COMISSAO_GLOBAIS)
    .select('modelo_carregador, valor_comissao_parceiro_pai');
  const default7 = errGlobais ? 0 : (globais ?? []).find((g: any) => g.modelo_carregador === 'Wallbox 7kW')?.valor_comissao_parceiro_pai ?? 0;
  const default11 = errGlobais ? 0 : (globais ?? []).find((g: any) => g.modelo_carregador === 'Wallbox 11kW')?.valor_comissao_parceiro_pai ?? 0;
  const default22 = errGlobais ? 0 : (globais ?? []).find((g: any) => g.modelo_carregador === 'Wallbox 22kW')?.valor_comissao_parceiro_pai ?? 0;
  const defaults = { 7: Number(default7), 11: Number(default11), 22: Number(default22) };

  const { data: parceirosPai, error: errPai } = await supabase
    .from('profiles')
    .select('id, nome')
    .eq('role', 'parceiro')
    .eq('pode_criar_rede_parceiros', true)
    .order('nome');

  if (errPai) throw errPai;
  if (!parceirosPai?.length) return [];

  const { data: comissoesPai, error: errComissoes } = await supabase
    .from(TABLE_COMISSAO_PAI)
    .select('parceiro_pai_id, parceiro_filho_id, modelo_carregador, valor_comissao');

  if (errComissoes) throw errComissoes;

  const mapComissoes = new Map<string, Map<string, { 7?: number; 11?: number; 22?: number }>>();
  (comissoesPai ?? []).forEach((c: any) => {
    const keyPai = c.parceiro_pai_id;
    const keyFilho = c.parceiro_filho_id;
    if (!mapComissoes.has(keyPai)) mapComissoes.set(keyPai, new Map());
    const mapFilhos = mapComissoes.get(keyPai)!;
    if (!mapFilhos.has(keyFilho)) mapFilhos.set(keyFilho, {});
    const v = mapFilhos.get(keyFilho)!;
    if (c.modelo_carregador === 'Wallbox 7kW') v[7] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 11kW') v[11] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 22kW') v[22] = Number(c.valor_comissao);
  });

  const resultado: ParceiroPaiComissao[] = [];

  for (const pai of parceirosPai) {
    const { data: filhos, error: errFilhos } = await supabase
      .from('profiles')
      .select('id, nome')
      .eq('role', 'parceiro')
      .eq('parceiro_cadastrado_por', pai.id)
      .order('nome');

    if (errFilhos) throw errFilhos;

    const valoresFilhos = mapComissoes.get(pai.id);
    const listaFilhos: ParceiroFilhoComissaoPai[] = (filhos ?? []).map((f: any) => {
      const cfg = valoresFilhos?.get(f.id) ?? {};
      return {
        parceiro_filho_id: f.id,
        parceiro_filho_nome: f.nome ?? 'Sem nome',
        valor_7kw: cfg[7] ?? defaults[7],
        valor_11kw: cfg[11] ?? defaults[11],
        valor_22kw: cfg[22] ?? defaults[22],
      };
    });

    resultado.push({
      parceiro_pai_id: pai.id,
      parceiro_pai_nome: pai.nome ?? 'Sem nome',
      filhos: listaFilhos,
    });
  }

  const { data: resumos, error: errResumos } = await supabase.rpc('get_carregadores_comissao_resumo_parceiro_pai', {
    p_parceiro_pai_id: null,
  });
  if (!errResumos && resumos?.length) {
    const mapResumo = new Map<string, { qtd: number; total: number; pago: number; saldo: number }>();
    (resumos as any[]).forEach((r: any) => {
      const key = `${r.parceiro_pai_id}-${r.parceiro_filho_id}`;
      mapResumo.set(key, {
        qtd: Number(r.qtd_contratos) || 0,
        total: Number(r.total_comissoes) || 0,
        pago: Number(r.total_pago) || 0,
        saldo: Number(r.saldo) || 0,
      });
    });
    resultado.forEach((pai) => {
      pai.filhos.forEach((filho) => {
        const key = `${pai.parceiro_pai_id}-${filho.parceiro_filho_id}`;
        const res = mapResumo.get(key);
        if (res) {
          filho.qtd_contratos = res.qtd;
          filho.total_comissoes = res.total;
          filho.total_pago = res.pago;
          filho.saldo = res.saldo;
        } else {
          filho.qtd_contratos = 0;
          filho.total_comissoes = 0;
          filho.total_pago = 0;
          filho.saldo = 0;
        }
      });
    });
  }

  return resultado;
}

/**
 * Salva o valor de comissão que o parceiro pai recebe por contrato fechado por um parceiro filho (por modelo).
 * Apenas admin pode chamar.
 */
export async function salvarComissaoParceiroPai(
  parceiroPaiId: string,
  parceiroFilhoId: string,
  modelo: ModeloCarregador,
  valorComissao: number
): Promise<void> {
  const valor = Math.max(0, valorComissao);

  const { data: existing } = await supabase
    .from(TABLE_COMISSAO_PAI)
    .select('id')
    .eq('parceiro_pai_id', parceiroPaiId)
    .eq('parceiro_filho_id', parceiroFilhoId)
    .eq('modelo_carregador', modelo)
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase
      .from(TABLE_COMISSAO_PAI)
      .update({ valor_comissao: valor })
      .eq('id', existing.id);
    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from(TABLE_COMISSAO_PAI)
    .insert({
      parceiro_pai_id: parceiroPaiId,
      parceiro_filho_id: parceiroFilhoId,
      modelo_carregador: modelo,
      valor_comissao: valor,
    });
  if (error) throw error;
}

/**
 * Retorna os valores padrão globais de comissão para parceiros (por modelo), para uso quando o parceiro não tem valor específico.
 */
async function obterDefaultsComissaoParceiro(): Promise<{ 7: number; 11: number; 22: number }> {
  const { data, error } = await supabase
    .from(TABLE_COMISSAO_GLOBAIS)
    .select('modelo_carregador, valor_comissao_parceiro');
  if (error) return { 7: 0, 11: 0, 22: 0 };
  const list = data ?? [];
  return {
    7: Number(list.find((r: any) => r.modelo_carregador === 'Wallbox 7kW')?.valor_comissao_parceiro) || 0,
    11: Number(list.find((r: any) => r.modelo_carregador === 'Wallbox 11kW')?.valor_comissao_parceiro) || 0,
    22: Number(list.find((r: any) => r.modelo_carregador === 'Wallbox 22kW')?.valor_comissao_parceiro) || 0,
  };
}

/**
 * Lista parceiros com valores de comissão por modelo apenas da rede do parceiro pai especificado.
 * Usado quando um parceiro pai visualiza comissões de sua rede.
 * Usa valores padrão globais quando o parceiro não tem valor específico configurado.
 */
export async function listarParceirosRedeComComissaoPorModelo(parceiroPaiId: string): Promise<ParceiroComissaoPorModelo[]> {
  // Buscar parceiros filhos da rede
  const { data: filhos, error: errFilhos } = await supabase
    .from('profiles')
    .select('id, nome, role')
    .eq('role', 'parceiro')
    .eq('parceiro_cadastrado_por', parceiroPaiId)
    .order('nome');

  if (errFilhos) throw errFilhos;
  if (!filhos?.length) return [];

  const defaults = await obterDefaultsComissaoParceiro();

  // Buscar comissões dos filhos
  const { data: comissoes, error: errComissoes } = await supabase
    .from(TABLE)
    .select('parceiro_id, modelo_carregador, valor_comissao')
    .in('parceiro_id', filhos.map(f => f.id));

  if (errComissoes) throw errComissoes;

  const map = new Map<string, { 7: number; 11: number; 22: number }>();
  (comissoes ?? []).forEach((c: any) => {
    const key = c.parceiro_id;
    if (!map.has(key)) map.set(key, { 7: 0, 11: 0, 22: 0 });
    const v = map.get(key)!;
    if (c.modelo_carregador === 'Wallbox 7kW') v[7] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 11kW') v[11] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 22kW') v[22] = Number(c.valor_comissao);
  });

  return filhos.map((p: any) => {
    const cfg = map.get(p.id) ?? { 7: 0, 11: 0, 22: 0 };
    return {
      id: p.id,
      nome: p.nome ?? 'Sem nome',
      role: p.role,
      valor_7kw: cfg[7] || defaults[7],
      valor_11kw: cfg[11] || defaults[11],
      valor_22kw: cfg[22] || defaults[22],
    };
  });
}

/**
 * Lista comissões do parceiro pai especificado com seus parceiros filho e valores de comissão configurados.
 * Quando não houver valor específico para (pai, filho, modelo), usa o valor padrão global.
 * Usado quando um parceiro pai visualiza comissões de sua rede.
 */
export async function listarMinhasComissoesParceiroPai(parceiroPaiId: string): Promise<ParceiroPaiComissao | null> {
  // Verificar se o parceiro é um pai
  const { data: pai, error: errPai } = await supabase
    .from('profiles')
    .select('id, nome')
    .eq('id', parceiroPaiId)
    .eq('role', 'parceiro')
    .eq('pode_criar_rede_parceiros', true)
    .single();

  if (errPai || !pai) return null;

  // Buscar valores padrão globais
  const { data: globais, error: errGlobais } = await supabase
    .from(TABLE_COMISSAO_GLOBAIS)
    .select('modelo_carregador, valor_comissao_parceiro_pai');
  const default7 = errGlobais ? 0 : (globais ?? []).find((g: any) => g.modelo_carregador === 'Wallbox 7kW')?.valor_comissao_parceiro_pai ?? 0;
  const default11 = errGlobais ? 0 : (globais ?? []).find((g: any) => g.modelo_carregador === 'Wallbox 11kW')?.valor_comissao_parceiro_pai ?? 0;
  const default22 = errGlobais ? 0 : (globais ?? []).find((g: any) => g.modelo_carregador === 'Wallbox 22kW')?.valor_comissao_parceiro_pai ?? 0;
  const defaults = { 7: Number(default7), 11: Number(default11), 22: Number(default22) };

  // Buscar filhos da rede
  const { data: filhos, error: errFilhos } = await supabase
    .from('profiles')
    .select('id, nome')
    .eq('role', 'parceiro')
    .eq('parceiro_cadastrado_por', pai.id)
    .order('nome');

  if (errFilhos) throw errFilhos;

  // Buscar comissões configuradas
  const { data: comissoesPai, error: errComissoes } = await supabase
    .from(TABLE_COMISSAO_PAI)
    .select('parceiro_pai_id, parceiro_filho_id, modelo_carregador, valor_comissao')
    .eq('parceiro_pai_id', pai.id);

  if (errComissoes) throw errComissoes;

  const mapComissoes = new Map<string, { 7?: number; 11?: number; 22?: number }>();
  (comissoesPai ?? []).forEach((c: any) => {
    const keyFilho = c.parceiro_filho_id;
    if (!mapComissoes.has(keyFilho)) mapComissoes.set(keyFilho, {});
    const v = mapComissoes.get(keyFilho)!;
    if (c.modelo_carregador === 'Wallbox 7kW') v[7] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 11kW') v[11] = Number(c.valor_comissao);
    else if (c.modelo_carregador === 'Wallbox 22kW') v[22] = Number(c.valor_comissao);
  });

  const listaFilhos: ParceiroFilhoComissaoPai[] = (filhos ?? []).map((f: any) => {
    const cfg = mapComissoes.get(f.id) ?? {};
    return {
      parceiro_filho_id: f.id,
      parceiro_filho_nome: f.nome ?? 'Sem nome',
      valor_7kw: cfg[7] ?? defaults[7],
      valor_11kw: cfg[11] ?? defaults[11],
      valor_22kw: cfg[22] ?? defaults[22],
    };
  });

  const { data: resumos, error: errResumos } = await supabase.rpc('get_carregadores_comissao_resumo_parceiro_pai', {
    p_parceiro_pai_id: pai.id,
  });
  if (!errResumos && resumos?.length) {
    const mapResumo = new Map<string, { qtd: number; total: number; pago: number; saldo: number }>();
    (resumos as any[]).forEach((r: any) => {
      mapResumo.set(r.parceiro_filho_id, {
        qtd: Number(r.qtd_contratos) || 0,
        total: Number(r.total_comissoes) || 0,
        pago: Number(r.total_pago) || 0,
        saldo: Number(r.saldo) || 0,
      });
    });
    listaFilhos.forEach((filho) => {
      const res = mapResumo.get(filho.parceiro_filho_id);
      if (res) {
        filho.qtd_contratos = res.qtd;
        filho.total_comissoes = res.total;
        filho.total_pago = res.pago;
        filho.saldo = res.saldo;
      } else {
        filho.qtd_contratos = 0;
        filho.total_comissoes = 0;
        filho.total_pago = 0;
        filho.saldo = 0;
      }
    });
  }

  return {
    parceiro_pai_id: pai.id,
    parceiro_pai_nome: pai.nome ?? 'Sem nome',
    filhos: listaFilhos,
  };
}

/**
 * Resumo de comissões carregadores apenas da rede do parceiro pai especificado.
 * Retorna resumo dos parceiros filhos da rede.
 */
export async function getResumoComissaoCarregadoresRede(parceiroPaiId: string): Promise<ResumoComissaoCarregador[]> {
  // Buscar IDs dos parceiros filhos da rede
  const { data: filhos, error: errFilhos } = await supabase
    .from('profiles')
    .select('id')
    .eq('role', 'parceiro')
    .eq('parceiro_cadastrado_por', parceiroPaiId);

  if (errFilhos) throw errFilhos;
  if (!filhos?.length) return [];

  const filhosIds = filhos.map(f => f.id);

  // Buscar resumo para cada filho
  const resultados: ResumoComissaoCarregador[] = [];
  for (const filhoId of filhosIds) {
    const { data, error } = await supabase.rpc('get_carregadores_comissao_resumo', {
      p_parceiro_id: filhoId,
    });
    if (error) throw error;
    if (data && data.length > 0) {
      resultados.push({
        parceiro_id: data[0].parceiro_id,
        parceiro_nome: data[0].parceiro_nome ?? 'Sem nome',
        parceiro_pai_nome: data[0].parceiro_pai_nome ?? null,
        qtd_contratos: Number(data[0].qtd_contratos) || 0,
        valor_comissao_unitario: Number(data[0].valor_comissao_unitario) || 0,
        total_comissoes: Number(data[0].total_comissoes) || 0,
        total_pago: Number(data[0].total_pago) || 0,
        saldo: Number(data[0].saldo) || 0,
      });
    }
  }

  return resultados;
}
